// // TODD: Move this to the server and simply pass down the custom styles
import { isReadable, mostReadable } from '@ctrl/tinycolor';
import {
  computeForegroundColor,
  transparentize,
  lighten,
  darken,
} from '@/utils/color';

type BrandingSettings = {
  buttonRadius?: string;
  cardRadius?: string;
  font?: string;
  primaryColor?: string;
  secondaryColor?: string;
};

type FloatingButtonSettings = {
  circleOffsetX?: string;
  circleOffsetY?: string;
  tabOffsetY?: string;
};

/** Generates all CSS variables based on settings */
export function generateCssVariables(
  branding: BrandingSettings = {},
  floatingButton: FloatingButtonSettings = {},
  cbTheme: string = 'light',
  pcTheme: string = 'classic',
): string {
  const { buttonRadius, cardRadius, font, primaryColor, secondaryColor } =
    branding;
  const { circleOffsetX, circleOffsetY, tabOffsetY } = floatingButton;

  const cssVariables = `
    :host {
      ${font ? `--font: ${font};` : ''}
      ${circleOffsetX ? `--circle-offset-x: ${circleOffsetX};` : ''}
      ${circleOffsetY ? `--circle-offset-y: ${circleOffsetY};` : ''}
      ${tabOffsetY ? `--tab-offset-y: ${tabOffsetY};` : ''}
      ${buttonRadius ? `--button-radius: ${buttonRadius};` : ''}
      ${cardRadius ? `--card-radius: ${cardRadius};` : ''}

      ${primaryColor ? `--primary: ${primaryColor};` : ''}
      ${primaryColor ? `--primary-foreground: ${computeForegroundColor(primaryColor)};` : ''}
      ${primaryColor ? `--primary-hover: ${lighten(primaryColor, 10)};` : ''}
      ${primaryColor ? `--primary-transparent: ${transparentize(primaryColor, 0.1)};` : ''}
      
      ${secondaryColor ? `--secondary: ${secondaryColor};` : ''}
      ${secondaryColor ? `--secondary-foreground: ${computeForegroundColor(secondaryColor)};` : ''}
      ${secondaryColor ? `--secondary-muted-foreground: ${computeForegroundColor(secondaryColor) === '#f9fafb' ? darken('#f9fafb', 20) : lighten('#1f2937', 20)};` : ''}
    }

    ${
      cbTheme === 'brand' && secondaryColor
        ? (() => {
            const mostReadableColor = mostReadable(secondaryColor, [
              '#f9fafb',
              '#1f2937',
            ])?.toHexString();

            const isDark = mostReadableColor === '#f9fafb';

            return `
                .brand {
                  --cb-button-outline-border: ${isDark ? lighten(secondaryColor, 20) : darken(secondaryColor, 20)};
                  --cb-border: ${isDark ? lighten(secondaryColor, 10) : darken(secondaryColor, 10)};
                }`;
          })()
        : ''
    }

    ${(() => {
      if (!pcTheme || !secondaryColor || !primaryColor) return '';

      switch (pcTheme) {
        case 'brand': {
          const mostReadableColor = mostReadable(secondaryColor, [
            '#f9fafb',
            '#1f2937',
          ])?.toHexString();

          const isDark = mostReadableColor === '#f9fafb';

          return `
          .brand {
            --banner: var(--background);
            --tab-active: ${
              isReadable(primaryColor, secondaryColor, {
                level: 'AA',
                size: 'large',
              })
                ? primaryColor
                : mostReadableColor
            };
            --card: ${isDark ? lighten(secondaryColor, 2) : darken(secondaryColor, 2)};
            --pc-button-outline-border: ${isDark ? '#374151' : '#d1d5db'};
            --input: ${isDark ? '#374151' : '#d1d5db'};
            --card-border: ${isDark ? lighten(secondaryColor, 10) : darken(secondaryColor, 10)};
            --pc-border: ${isDark ? lighten(secondaryColor, 10) : darken(secondaryColor, 10)};
          }`;
        }

        case 'classic':
          return `
          .classic {
            --banner: ${secondaryColor};
            --tab-active: ${
              isReadable(primaryColor, secondaryColor)
                ? primaryColor
                : mostReadable(secondaryColor, [
                    '#f9fafb',
                    '#1f2937',
                  ])?.toHexString()
            };
            --tab-inactive: ${
              mostReadable(secondaryColor, [
                '#f9fafb',
                '#1f2937',
              ])?.toHexString() === '#1f2937'
                ? lighten('#1f2937', 40)
                : darken('#f9fafb', 30)
            };
          }`;

        case 'light':
          return `
          .light {
            --banner: var(--background);
            ${isReadable('#fff', primaryColor, { level: 'AA', size: 'large' }) ? `--tab-active: ${primaryColor};` : ''}
          }`;

        case 'dark':
          return `
          .dark {
            --banner: var(--background);
            ${isReadable('#030712', primaryColor) ? `--tab-active: ${primaryColor};` : ''}
          }`;

        default:
          return '';
      }
    })()}
  `;

  return cssVariables;
}
